import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DotsThree, Flag, LinkSimple, PaperPlaneTilt, PencilSimple, Prohibit, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { reelsApi, toAbsolute } from "@/lib/apiClient";

export function getReelDeleteErrorMessage(error) {
    const status = error?.response?.status;
    if (status === 401) return "You need to sign in before deleting a reel.";
    if (status === 403) return error?.response?.data?.error || "You can only delete your own reels.";
    if (status === 404) return "Reel no longer available.";
    if (status === 409) return error?.response?.data?.error || "This reel cannot currently be deleted.";
    return "Unable to delete Reel. Please try again.";
}

export function getReelEditErrorMessage(error) {
    const status = error?.response?.status;
    if (status === 401) return "You need to sign in before editing a reel.";
    if (status === 403) return "You can only edit your own reels.";
    if (status === 404) return "Reel no longer available.";
    return "Unable to update Reel. Please try again.";
}

export function ReelOptionsMenu({ reel, isOwner, onEdit, onDelete, onReport, onNotInterested, onSend, onShareLink }) {
    const [open, setOpen] = useState(false);

    return (
        <div className="relative" data-testid={`reel-menu-shell-${reel.id}`}>
            <button
                onClick={() => setOpen((prev) => !prev)}
                className="w-10 h-10 rounded-full grid place-items-center bg-black/30 border border-white/10"
                data-testid={`reel-menu-${reel.id}`}
                aria-label="Reel menu"
            >
                <DotsThree size={18} weight="bold" />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="absolute right-0 top-12 rounded-2xl p-1 min-w-[190px] z-20"
                        style={{ background: "rgba(5,10,17,0.96)", border: "1px solid rgba(255,255,255,0.08)" }}
                        data-testid={`reel-menu-panel-${reel.id}`}
                    >
                        {isOwner ? (
                            <>
                                <MenuAction testId={`reel-send-${reel.id}`} icon={<PaperPlaneTilt size={14} />} label="Send in Passport Messages" onClick={() => { setOpen(false); onSend?.(reel); }} />
                                <MenuAction testId={`reel-link-${reel.id}`} icon={<LinkSimple size={14} />} label="Share Reel Link" onClick={() => { setOpen(false); onShareLink?.(reel); }} />
                                <MenuAction testId={`reel-edit-${reel.id}`} icon={<PencilSimple size={14} />} label="Edit Reel" onClick={() => { setOpen(false); onEdit?.(reel); }} />
                                <MenuAction testId={`reel-delete-${reel.id}`} icon={<Trash size={14} />} label="Delete Reel" destructive onClick={() => { setOpen(false); onDelete?.(reel); }} />
                            </>
                        ) : (
                            <>
                                <MenuAction testId={`reel-send-${reel.id}`} icon={<PaperPlaneTilt size={14} />} label="Send in Passport Messages" onClick={() => { setOpen(false); onSend?.(reel); }} />
                                <MenuAction testId={`reel-link-${reel.id}`} icon={<LinkSimple size={14} />} label="Share Reel Link" onClick={() => { setOpen(false); onShareLink?.(reel); }} />
                                <MenuAction testId={`reel-report-${reel.id}`} icon={<Flag size={14} />} label="Report Reel" onClick={() => { setOpen(false); onReport?.(reel); }} />
                                <MenuAction testId={`reel-not-interested-${reel.id}`} icon={<Prohibit size={14} />} label="Not Interested" onClick={() => { setOpen(false); onNotInterested?.(reel); }} />
                            </>
                        )}
                        <MenuAction testId={`reel-menu-cancel-${reel.id}`} label="Cancel" onClick={() => setOpen(false)} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function MenuAction({ icon, label, destructive = false, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            className={`w-full text-left text-sm px-3 py-2.5 rounded-xl hover:bg-white/5 flex items-center gap-2 ${destructive ? "text-red-300" : "text-white/90"}`}
            data-testid={testId}
        >
            {icon}
            <span>{label}</span>
        </button>
    );
}

export function DeleteReelDialog({ open, reel, deleting = false, draft = false, onClose, onConfirm }) {
    if (!open || !reel) return null;
    const title = draft ? "Delete Draft?" : "Delete Reel?";
    const description = draft
        ? "This draft will be permanently removed."
        : "Are you sure you want to delete this Reel? This action cannot be undone.";

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[98] flex items-center justify-center p-4"
                style={{ background: "rgba(5,10,17,0.86)", backdropFilter: "blur(10px)" }}
                onClick={deleting ? undefined : onClose}
                data-testid="reel-delete-dialog"
            >
                <motion.div
                    initial={{ y: 18, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 18, opacity: 0 }}
                    onClick={(event) => event.stopPropagation()}
                    className="w-full max-w-md rounded-[30px] p-7"
                    style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.2)" }}
                >
                    <div className="overline mb-2">/ Confirm</div>
                    <h3 className="font-serif text-3xl text-white leading-tight mb-3" data-testid="reel-delete-title">{title}</h3>
                    <p className="text-white/65 text-sm leading-relaxed" data-testid="reel-delete-description">{description}</p>
                    <div className="mt-6 flex gap-3">
                        <button onClick={onClose} disabled={deleting} className="btn-ghost flex-1 justify-center" data-testid="reel-delete-cancel">Cancel</button>
                        <button
                            onClick={() => onConfirm?.(reel)}
                            disabled={deleting}
                            className="flex-1 justify-center inline-flex items-center rounded-full px-5 py-3 text-sm font-medium transition-colors"
                            style={{ background: deleting ? "rgba(239,68,68,0.35)" : "#7f1d1d", color: "#ffe4e6", border: "1px solid rgba(248,113,113,0.45)" }}
                            data-testid="reel-delete-confirm"
                        >
                            {deleting ? "Deleting…" : "Delete"}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export function EditReelModal({ open, reel, onClose, onSaved }) {
    const [caption, setCaption] = useState("");
    const [location, setLocation] = useState("");
    const [country, setCountry] = useState("");
    const [destination, setDestination] = useState("");
    const [destinationTags, setDestinationTags] = useState("");
    const [privacy, setPrivacy] = useState("public");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open || !reel) return;
        setCaption(reel.caption || "");
        setLocation(reel.location || "");
        setCountry(reel.country || "");
        setDestination(reel.destination || "");
        setDestinationTags((reel.destination_tags || []).join(", "));
        setPrivacy(reel.privacy || "public");
        setBusy(false);
    }, [open, reel]);

    if (!open || !reel) return null;

    const submit = async (event) => {
        event?.preventDefault?.();
        if (!location && !country && !destination) return toast.error("Please provide a location, country, or destination.");
        setBusy(true);
        try {
            const updated = await reelsApi.update(reel.id, {
                caption,
                location: location || null,
                country: country || null,
                destination: destination || null,
                destination_tags: destinationTags.split(",").map((item) => item.trim()).filter(Boolean),
                privacy,
            });
            toast.success("Reel updated.");
            onSaved?.({ ...reel, ...updated });
            onClose?.();
        } catch (error) {
            toast.error(getReelEditErrorMessage(error));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[97] flex items-center justify-center p-4"
                style={{ background: "rgba(5,10,17,0.84)", backdropFilter: "blur(10px)" }}
                onClick={onClose}
                data-testid="edit-reel-modal"
            >
                <motion.form
                    initial={{ y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 18, opacity: 0 }}
                    onClick={(event) => event.stopPropagation()}
                    onSubmit={submit}
                    className="w-full max-w-2xl rounded-[32px] p-8 max-h-[92vh] overflow-y-auto"
                    style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.28)" }}
                >
                    <div className="flex items-start justify-between gap-4 mb-6">
                        <div>
                            <div className="overline mb-2">/ Edit reel</div>
                            <h3 className="font-serif text-3xl text-white" data-testid="edit-reel-title">Refine the moment.</h3>
                            <p className="text-white/55 text-sm mt-2 max-w-lg">Update the destination details and caption without losing the Reel itself.</p>
                        </div>
                        <button type="button" onClick={onClose} className="w-10 h-10 rounded-full glass grid place-items-center" data-testid="edit-reel-close">×</button>
                    </div>

                    <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-6">
                        <div className="rounded-[28px] overflow-hidden bg-black/35 border border-white/10">
                            <div className="aspect-[9/16] bg-black">
                                <video src={toAbsolute(reel.media_url)} poster={toAbsolute(reel.poster_url) || undefined} controls muted playsInline className="w-full h-full object-cover" data-testid="edit-reel-preview" />
                            </div>
                        </div>
                        <div className="space-y-4">
                            <textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={5} placeholder="Caption" className="reel-input" data-testid="edit-reel-caption" />
                            <input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Location" className="reel-input" data-testid="edit-reel-location" />
                            <input value={country} onChange={(event) => setCountry(event.target.value)} placeholder="Country" className="reel-input" data-testid="edit-reel-country" />
                            <input value={destination} onChange={(event) => setDestination(event.target.value)} placeholder="Destination headline" className="reel-input" data-testid="edit-reel-destination" />
                            <input value={destinationTags} onChange={(event) => setDestinationTags(event.target.value)} placeholder="Destination tags, comma separated" className="reel-input" data-testid="edit-reel-tags" />
                            <div className="space-y-2">
                                <div className="text-xs uppercase tracking-[0.24em] text-white/45">Privacy</div>
                                <div className="grid grid-cols-2 gap-2" data-testid="edit-reel-privacy-options">
                                    {[
                                        { key: "public", label: "Public" },
                                        { key: "followers", label: "Followers" },
                                        { key: "friends", label: "Friends" },
                                        { key: "private", label: "Private" },
                                    ].map((item) => (
                                        <button
                                            key={item.key}
                                            type="button"
                                            onClick={() => setPrivacy(item.key)}
                                            className="px-4 py-3 rounded-2xl text-sm transition-colors"
                                            style={{ background: privacy === item.key ? "var(--gold)" : "rgba(255,255,255,0.05)", color: privacy === item.key ? "var(--navy)" : "white" }}
                                            data-testid={`edit-reel-privacy-${item.key}`}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center" data-testid="edit-reel-cancel">Cancel</button>
                        <button type="submit" disabled={busy} className="btn-gold flex-1 justify-center" data-testid="edit-reel-save">{busy ? "Saving…" : "Save changes"}</button>
                    </div>

                    <style>{`.reel-input { width: 100%; background: rgba(0,0,0,0.28); border: 1px solid rgba(255,255,255,0.08); border-radius: 18px; padding: 12px 14px; color: white; font-size: 14px; } .reel-input::placeholder { color: rgba(255,255,255,0.34); } .reel-input:focus { outline: none; border-color: var(--gold); }`}</style>
                </motion.form>
            </motion.div>
        </AnimatePresence>
    );
}

export function ReelUnavailableState({ title = "Reel no longer available.", description = "This Reel has been removed or can’t be opened anymore.", onBack }) {
    return (
        <div className="rounded-[32px] p-8 md:p-10 text-center" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="reel-unavailable-state">
            <div className="overline mb-2">/ Reels</div>
            <div className="font-serif text-4xl md:text-5xl text-white leading-[0.95] mb-3" data-testid="reel-unavailable-title">{title}</div>
            <p className="text-white/60 text-sm md:text-base max-w-xl mx-auto mb-6" data-testid="reel-unavailable-description">{description}</p>
            <button onClick={onBack} className="btn-gold text-sm !py-2.5 !px-5" data-testid="reel-unavailable-back">Back to Travel Feed</button>
        </div>
    );
}
